import Vue from 'vue';
import DashboardSetupService from '@/api/dashboard';
import ObjectSetupService from '@/api/objectsetup';

import DateHelper from '@/helper/dateHelper';
import ObjectHelper from '@/helper/objectHelper';

const gMaxObjects = 10000;

const deepClone = obj => JSON.parse(JSON.stringify(obj));

export default {
	state: {
		searchString: '',
		language: 'de', /* NOTE: So belassen, oder auch einen Sprachwechsler für das Dashboard? */
		objectData: [],
		objectDataUnfrozen: [],
		loadingObjectData: false,
		activeFilters: {},
		selectedObjectIds: [],
		cmsStructure: {
			structure: {},
			loading: false,
		},
		userSpecifications: [],
		lastFetchAt: null,
		actionInProcess: false,
	},

	mutations: {
		setObjectData(state, objectData) {
			const objects = Object.keys(objectData)
							.map(key => ({
								key,
								...objectData[key],
								S_Status: (objectData[key].S_Status || ('(unknown)')),
							}))
							.sort((a, b) => {
								const aTime = (new Date(a.TS_zuletzt_bearbeitet)).getTime();
								const bTime = (new Date(b.TS_zuletzt_bearbeitet)).getTime();
								return bTime - aTime;
							});

			state.objectDataUnfrozen = objects.slice(0, gMaxObjects);
			state.objectData = Object.freeze(deepClone(state.objectDataUnfrozen));

			state.lastFetchAt = (new Date()).getTime();
		},
		setLastFetchAt(state, newLastFetchAt) {
			state.lastFetchAt = newLastFetchAt;
		},
		setSearchString(state, searchString) {
			state.searchString = searchString;
		},
		addDashboardFilter(state, filter) {
			Vue.set(state.activeFilters, filter.type, filter);
		},
		removeDashboardFilter(state, filterType) {
			if (state.activeFilters[filterType]) {
				Vue.delete(state.activeFilters, filterType);
			}
		},
		resetDashboardFilters(state) {
			state.activeFilters = {};
		},
		addObjectToSelection(state, id) {
			state.selectedObjectIds.push(id);
		},
		removeObjectToSelection(state, id) {
			const indexOfObject = state.selectedObjectIds.indexOf(id);

			if (indexOfObject > -1) {
				state.selectedObjectIds.splice(indexOfObject, 1);
			}
		},
		emptyObjectSelection(state) {
			state.selectedObjectIds = [];
		},
		updateLoadingObjectData(state, status) {
			state.loadingObjectData = status;
		},
		setCMSStructure(state, structure) {
			state.cmsStructure.structure = structure;
		},
		setCMSStructureLoadingStatus(state, status) {
			state.cmsStructure.loading = status;
		},
		updateDashboardObjectsStatus(state, datasets) {
			datasets.forEach((dataset) => {
				const currentObject =
					state.objectDataUnfrozen.find(currObject =>
						(currObject.PKM_Objekt === dataset.PKM_Objekt.toString()));

				if (currentObject) {
					Vue.set(currentObject, 'S_Status', dataset.S_Status);
					Vue.set(currentObject, 'FKM_Status', dataset.FKM_Status);
				}
			});
			state.objectData = Object.freeze(deepClone(state.objectDataUnfrozen));
		},
		copyObject(state, data) {
			data.relations.forEach((relation) => {
				const oldObjectId = relation.oldObjectId;
				const newObjectId = relation.newObjectId;

				const oldObject = state.objectDataUnfrozen.find(obj =>
					(obj.PKM_Objekt === oldObjectId.toString()));

				const newObject = deepClone(oldObject);
				newObject.PKM_Objekt = newObjectId.toString();
				newObject.TS_Aenderungsdatum = DateHelper.format('yyyy.mm.dd hh:MM:ss', new Date());
				newObject.TS_zuletzt_bearbeitet = DateHelper.format('yyyy.mm.dd hh:MM:ss', new Date());
				newObject.isFreshlyCopied = true;

				switch (data.mode) {
					case 'full':
						newObject.S_de_Objekt = `${newObject.S_de_Objekt} Kopie`;
						break;
					case 'empty':
						newObject.S_de_Objekt = `${newObject.S_de_Objekt} Leerkopie`;
						newObject.S_Stellvertreterbild = null;
						break;
					default: /* Do nothing :( */
				}

				state.objectDataUnfrozen.push(newObject);
			});

			state.objectDataUnfrozen =
				state.objectDataUnfrozen
				.sort((a, b) => {
					const aTime = (new Date(a.TS_zuletzt_bearbeitet)).getTime();
					const bTime = (new Date(b.TS_zuletzt_bearbeitet)).getTime();
					return bTime - aTime;
				});

			state.objectData = Object.freeze(deepClone(state.objectDataUnfrozen));
		},

		setUserSpecifications(state, userSpecifications) {
			state.userSpecifications = userSpecifications;
		},

		setObjectNames(state, objectsIdNameMap) {
			objectsIdNameMap.forEach((objIdNamePair) => {
				const objId = objIdNamePair.id.toString();
				const newName = objIdNamePair.name[state.language];
				const foundObject = state.objectDataUnfrozen.find(obj => obj.PKM_Objekt === objId);

				if (foundObject) {
					Vue.set(foundObject, `S_${state.language}_Objekt`, newName);
				}
			});

			state.objectData = Object.freeze(deepClone(state.objectDataUnfrozen));
		},

		setActionInProcess(state, actionInProcess) {
			state.actionInProcess = actionInProcess;
		},
	},
	actions: {
		initDashboard({ commit, state, getters }) {
			return new Promise((resolve, reject) => {
				const remainingSeconds =
					(process.env.DASHBOARD_OBJECT_REFETCH_TIME_THRESHOLD -
					 ((new Date()).getTime() - (state.lastFetchAt || 0))) / 1000;
				if (remainingSeconds > 0) {
					 /* TODO: Use (better) logging */
					resolve({ status: -1 });
					return;
				}

				commit('updateLoadingObjectData', true);
				DashboardSetupService.get()
				.then((response) => {
					Object.keys(response || {}).forEach((key) => {
						response[key] = {
							...response[key],
							isProductive: ObjectHelper.isProductive(response[key], getters.statiAssoc),
							isBeforeProduction: ObjectHelper.isBeforeProduction(response[key]),
							onlineUrl: ObjectHelper.onlineUrl(response[key], 'de'),
							previewUrl: ObjectHelper.previewUrl(response[key], 'de'),
						};
					});
					commit('setObjectData', response);
					commit('updateLoadingObjectData', false);
					resolve(response);
				})
				.catch((e) => {
					commit('updateLoadingObjectData', false);
					reject(e);
				});
			});
		},
		updateSearchString({ commit }, params) {
			commit('setSearchString', params.searchString);
		},
		addDashboardFilter({ commit }, filter) {
			commit('addDashboardFilter', filter);
		},
		removeDashboardFilter({ commit }, filterType) {
			commit('removeDashboardFilter', filterType);
		},
		resetDashboardFilters({ commit }) {
			commit('resetDashboardFilters');
			commit('setSearchString', '');
		},

		invalidateLastFetchAt({ commit, state }) {
			commit('setLastFetchAt', (state.lastFetchAt - process.env.DASHBOARD_OBJECT_REFETCH_TIME_THRESHOLD));
		},

		handleObjectSelection({ commit }, action) {
			switch (action.type) {
				case 'add':
					commit('addObjectToSelection', action.objID);
					break;
				case 'remove':
					commit('removeObjectToSelection', action.objID);
					break;
				case 'empty':
					commit('emptyObjectSelection');
					break;
				default: /* Do nothing :( */
			}
		},

		triggerActionOnDashboardObjects({ commit, dispatch, rootState }, action) {
			const selectedDashboardObjectIds = rootState.dashboard.selectedObjectIds;
			commit('setActionInProcess', true);

			return new Promise((resolve, reject) => {
				switch (action) {
					case 'clone':
						DashboardSetupService.batchCopyObject(selectedDashboardObjectIds)
						.then((responses) => {
							const idsRelations = responses.map(response =>
								({
									oldObjectId: response.sourceId,
									newObjectId: response.PKM_Objekt,
								}),
							);
							commit('copyObject', {
								relations: idsRelations,
								mode: 'full',
							});
							commit('setActionInProcess', false);
							resolve(responses);
						})
						.catch((e) => {
							commit('setActionInProcess', false);
							reject(e);
						});
						break;
					case 'emptyCopy':
						DashboardSetupService.batchCopyObjectEmpty(selectedDashboardObjectIds)
						.then((responses) => {
							const idsRelations = responses.map(response =>
								({
									oldObjectId: response.sourceId,
									newObjectId: response.PKM_Objekt,
								}),
							);
							commit('copyObject', {
								relations: idsRelations,
								mode: 'empty',
							});
							commit('setActionInProcess', false);
							resolve(responses);
						})
						.catch((e) => {
							commit('setActionInProcess', false);
							reject(e);
						});
						break;
					default:
						commit('setActionInProcess', false);
						reject(`Unbekannte Aktion: ${action}`);
				}
			});
		},

		changeStatusOnDashboardObjects({ commit, dispatch, state, rootState, getters }, statusId) {
			commit('setActionInProcess', true);

			return new Promise((resolve, reject) => {
				const statiName = rootState.objectstati.stati
				                           .find(stati => stati.PKM_Status === statusId).S_Status;

				const selectedDasboardObjects =
					state.selectedObjectIds.map(id =>
						({
							PKM_Objekt: id,
							FKM_Status: statusId,
						}),
					);

				const extendedSelectedDashboardObjects =
					selectedDasboardObjects.map(currObject =>
						({
							...currObject,
							S_Status: statiName,
						}),
					);

				ObjectSetupService
				.batchStatusSet(selectedDasboardObjects)
				.then((response) => {
					commit('updateDashboardObjectsStatus', extendedSelectedDashboardObjects);

					if (statusId === getters.statiAssoc.gelöscht) {
						commit('emptyObjectSelection');
					}

					commit('setActionInProcess', false);
					resolve(response);
				})
				.catch((e) => {
					commit('setActionInProcess', false);
					reject(e);
				});
			});
		},

		getCMSStructure({ commit }) {
			return new Promise((resolve, reject) => {
				commit('setCMSStructureLoadingStatus', true);
				DashboardSetupService.getCMSStructure()
				.then((response) => {
					commit('setCMSStructure', response);

					/* Und jetzt können wir auch gleich die User-Spezifikationen holen */
					DashboardSetupService.getUserSpecifications()
					.then((specResponse) => {
						/* Gattungen für die Spezifikationen sammeln */
						const extendedSpecifications =
							Object.keys(specResponse).map((specIndex) => {
								const specification = specResponse[specIndex];
								specification.gattungen = {};

								Object.keys(response).forEach((categoryId) => {
									const cmsSpecification =
										response[categoryId].spezifikationen[specification.PKM_Spezifikation];

									if (cmsSpecification && cmsSpecification.gattungen) {
										specification.gattungen =
											Object.keys(cmsSpecification.gattungen).map(gattungId =>
												({
													PKM_Gattung: gattungId,
													S_Gattung: cmsSpecification.gattungen[gattungId].name,
												}),
											);
									}
								});

								return specification;
							});

						commit('setUserSpecifications', extendedSpecifications);

						resolve(specResponse);
					})
					.catch((specResponse) => {
						reject(specResponse);
					});
				})
				.catch((e) => {
					reject(e);
				});
			});
		},

		updateDashboardObjectNames({ commit, state }, objectsIdNameMap) {
			if (state.objectData.length > 0) {
				commit('setObjectNames', objectsIdNameMap);
			}
		},
	},

	getters: {
		selectedDashboardObjects(state) {
			return state.selectedObjectIds.map((objId) => {
				const strObjId = objId.toString();
				return state.objectData.find(obj => obj.PKM_Objekt === strObjId);
			});
		},
	},
};



// WEBPACK FOOTER //
// ./src/store/modules/dashboard.js